// === IMPORTS ===
import React, { useState, useRef, useEffect } from 'react';
import {
  Activity, ShieldCheck, AlertTriangle, AlertOctagon, ChevronDown, ChevronUp,
  Cpu, Maximize2, Users, Grid, Settings, CheckCircle2, XCircle, Info,
} from 'lucide-react';
import { SystemHealthMetrics, SystemHealthConfig } from '../types/systemHealth';
import { DEFAULT_HEALTH_CONFIG } from '../utils/systemHealthEngine';
import '../styles/twin-icons-shared.css';
import './SystemHealthBadge.css';

interface SystemHealthBadgeProps {
  metrics?: SystemHealthMetrics;
  healthMetrics?: SystemHealthMetrics;
  config?: SystemHealthConfig;
  healthConfig?: SystemHealthConfig;
  onOpenHealthSettings?: () => void;
  onOpenInspector?: () => void;
}

// === COMPONENTE: INSIGNIA DE SALUD DEL SISTEMA (HUD) ===
export const SystemHealthBadge: React.FC<SystemHealthBadgeProps> = ({
  metrics, healthMetrics: propHealthMetrics, config, healthConfig: propHealthConfig,
  onOpenHealthSettings, onOpenInspector,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeMetrics = metrics || propHealthMetrics;
  const activeConfig = config || propHealthConfig || DEFAULT_HEALTH_CONFIG;

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: MouseEvent | TouchEvent | PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (dropdownRef.current && dropdownRef.current.contains(target)) return;
      setIsOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!activeMetrics) return null;

  const {
    overallScore, status, statusLabel, statusColor, structuralScore, spatialScore,
    performanceScore, activityScore, placedAgentsCount, activeAgentsCount,
    areaPerPlacedAgentM2, roomOverlapCount, measuredFps, estimatedTickLatencyMs,
    incidents = [],
  } = activeMetrics;

  const getStatusIcon = () => {
    switch (status) {
      case 'optimal': return <ShieldCheck className="twin-icon-sm twin-health-icon--emerald" />;
      case 'good': return <Activity className="twin-icon-sm twin-health-icon--cyan" />;
      case 'degraded': return <AlertTriangle className="twin-icon-sm twin-health-icon--amber" />;
      case 'critical': return <AlertOctagon className="twin-icon-sm twin-health-icon--rose twin-bounce" />;
    }
  };

  const getScoreClass = (score: number) => {
    if (score >= 90) return 'twin-score--emerald';
    if (score >= 75) return 'twin-score--cyan';
    if (score >= 50) return 'twin-score--amber';
    return 'twin-score--rose';
  };

  // === RENDER ===
  return (
    <div className="twin-healthbadge" ref={dropdownRef} id="system-health-hud-container">
      {/* === INSIGNIA DISPARADORA === */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`twin-healthbadge-trigger twin-healthbadge-trigger--${status}`}
        title={`System Health: ${overallScore}% (${statusLabel}). Click to view telemetry and diagnostics.`}
        aria-expanded={isOpen}
      >
        <div className="twin-healthbadge-pulse-wrap">
          {activeConfig.showLivePulseAnimation && <span className={`twin-healthbadge-pulse-ring twin-healthbadge-pulse-ring--${status}`} />}
          <span className="twin-healthbadge-dot" style={{ backgroundColor: statusColor }} />
        </div>

        <div className="twin-healthbadge-score-group">
          <span className="twin-healthbadge-score-label">Health</span>
          <span className={`twin-healthbadge-score ${getScoreClass(overallScore)}`}>{overallScore}%</span>
          <span className="twin-healthbadge-status-tag" style={{ borderColor: `${statusColor}60`, backgroundColor: `${statusColor}20`, color: statusColor }}>
            {statusLabel}
          </span>
        </div>

        <div className="twin-healthbadge-chevron">{isOpen ? <ChevronUp className="twin-icon-sm" /> : <ChevronDown className="twin-icon-sm" />}</div>
      </button>

      {/* === POPOVER DE TELEMETRÍA === */}
      {isOpen && (
        <div className="twin-healthbadge-popover">
          {/* === HEADER === */}
          <div className="twin-healthbadge-popover-header">
            <div className="twin-healthbadge-popover-header-left">
              <div className="twin-healthbadge-status-icon-badge" style={{ backgroundColor: `${statusColor}18`, borderColor: `${statusColor}50` }}>
                {getStatusIcon()}
              </div>
              <div className="twin-healthbadge-popover-title-block">
                <h3 className="twin-healthbadge-popover-title">
                  <span>System Observability</span>
                  <span className="twin-healthbadge-preset-tag" style={{ backgroundColor: `${statusColor}25`, color: statusColor }}>{activeConfig.preset}</span>
                </h3>
                <p className="twin-healthbadge-popover-subtitle">Real-time SLA / Golden Signals</p>
              </div>
            </div>

            {onOpenHealthSettings && (
              <button type="button" onClick={() => { setIsOpen(false); onOpenHealthSettings(); }} className="twin-healthbadge-settings-btn" title="Configure Health Thresholds & Parameters">
                <Settings className="twin-icon-sm" />
              </button>
            )}
          </div>

          {/* === MEDIDOR PRINCIPAL === */}
          <div className="twin-healthbadge-gauge">
            <div className="twin-healthbadge-gauge-top">
              <div>
                <span className="twin-healthbadge-gauge-label">Overall Health Score</span>
                <div className="twin-healthbadge-gauge-value-row">
                  <span className="twin-healthbadge-gauge-value" style={{ color: statusColor }}>{overallScore}%</span>
                  <span className="twin-healthbadge-gauge-status">Status: {statusLabel}</span>
                </div>
              </div>
              <div className="twin-healthbadge-gauge-right">
                <span className="twin-healthbadge-gauge-label">Tick Performance</span>
                <p className="twin-healthbadge-gauge-fps">{measuredFps} FPS <span className="twin-healthbadge-gauge-fps-ms">({estimatedTickLatencyMs}ms)</span></p>
              </div>
            </div>
            <div className="twin-healthbadge-progress-track">
              <div className={`twin-healthbadge-progress-fill ${getScoreClass(overallScore)}`} style={{ width: `${overallScore}%` }} />
            </div>
          </div>

          {/* === 4 PILARES DE SALUD === */}
          <div className="twin-healthbadge-pillars-grid">
            <div className="twin-healthbadge-pillar">
              <div className="twin-healthbadge-pillar-header">
                <span className="twin-healthbadge-pillar-label"><Grid className="twin-icon-xs twin-health-icon--cyan" /><span>Structure</span></span>
                <span className={`twin-healthbadge-pillar-score ${getScoreClass(structuralScore)}`}>{structuralScore}%</span>
              </div>
              <div className="twin-healthbadge-mini-track"><div className={`twin-healthbadge-mini-fill ${getScoreClass(structuralScore)}`} style={{ width: `${structuralScore}%` }} /></div>
              <span className="twin-healthbadge-pillar-hint">{roomOverlapCount === 0 ? '✓ No overlaps' : `⚠️ ${roomOverlapCount} overlaps`}</span>
            </div>

            <div className="twin-healthbadge-pillar">
              <div className="twin-healthbadge-pillar-header">
                <span className="twin-healthbadge-pillar-label"><Maximize2 className="twin-icon-xs twin-health-icon--emerald" /><span>Capacity / Space</span></span>
                <span className={`twin-healthbadge-pillar-score ${getScoreClass(spatialScore)}`}>{spatialScore}%</span>
              </div>
              <div className="twin-healthbadge-mini-track"><div className={`twin-healthbadge-mini-fill ${getScoreClass(spatialScore)}`} style={{ width: `${spatialScore}%` }} /></div>
              <span className="twin-healthbadge-pillar-hint">{areaPerPlacedAgentM2.toFixed(1)} m²/agent (min: {activeConfig.minAreaPerAgentM2}m²)</span>
            </div>

            <div className="twin-healthbadge-pillar">
              <div className="twin-healthbadge-pillar-header">
                <span className="twin-healthbadge-pillar-label"><Cpu className="twin-icon-xs twin-health-icon--purple" /><span>Performance</span></span>
                <span className={`twin-healthbadge-pillar-score ${getScoreClass(performanceScore)}`}>{performanceScore}%</span>
              </div>
              <div className="twin-healthbadge-mini-track"><div className={`twin-healthbadge-mini-fill ${getScoreClass(performanceScore)}`} style={{ width: `${performanceScore}%` }} /></div>
              <span className="twin-healthbadge-pillar-hint">{measuredFps} FPS (target: {activeConfig.targetFps})</span>
            </div>

            <div className="twin-healthbadge-pillar">
              <div className="twin-healthbadge-pillar-header">
                <span className="twin-healthbadge-pillar-label"><Users className="twin-icon-xs twin-health-icon--amber" /><span>Agent Activity</span></span>
                <span className={`twin-healthbadge-pillar-score ${getScoreClass(activityScore)}`}>{activityScore}%</span>
              </div>
              <div className="twin-healthbadge-mini-track"><div className={`twin-healthbadge-mini-fill ${getScoreClass(activityScore)}`} style={{ width: `${activityScore}%` }} /></div>
              <span className="twin-healthbadge-pillar-hint">{placedAgentsCount} agents ({activeAgentsCount} active)</span>
            </div>
          </div>

          {/* === DIAGNÓSTICOS Y ALERTAS === */}
          <div className="twin-healthbadge-diagnostics">
            <div className="twin-healthbadge-diagnostics-header">
              <span className="twin-healthbadge-diagnostics-title">Diagnostics & Alerts ({incidents.length})</span>
              <span className="twin-healthbadge-diagnostics-live">Live Telemetry</span>
            </div>

            {incidents.length === 0 ? (
              <div className="twin-healthbadge-no-incidents"><CheckCircle2 className="twin-icon-sm" /><span>All subsystems are operating within optimal thresholds.</span></div>
            ) : (
              <div className="twin-healthbadge-incidents-list">
                {incidents.slice(0, 3).map((inc) => (
                  <div key={inc.id} className={`twin-healthbadge-incident twin-healthbadge-incident--${inc.level}`}>
                    {inc.level === 'critical' ? <XCircle className="twin-icon-sm" /> : inc.level === 'warning' ? <AlertTriangle className="twin-icon-sm" /> : <Info className="twin-icon-sm" />}
                    <div>
                      <p className="twin-healthbadge-incident-title">{inc.title}</p>
                      <p className="twin-healthbadge-incident-message">{inc.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* === FOOTER === */}
          <div className="twin-healthbadge-footer">
            <span className="twin-healthbadge-footer-time">Updated &lt; 1s ago</span>
            {onOpenHealthSettings && (
              <button type="button" onClick={() => { setIsOpen(false); onOpenHealthSettings(); }} className="twin-healthbadge-calibrate-btn">
                <Settings className="twin-icon-xs" /><span>Calibrate Parameters...</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};